// frontend/src/i18n/index.js
//
// Generic patient-facing UI translation framework. Not tied to any one
// page — built for the tracking page first, now also driving the kiosk
// flow itself (language select onward), reusable anywhere a page needs to
// render fully in the patient's chosen language.
//
// Adding a language:
//   1. Add an entry to LANGUAGES with its display name and text direction.
//   2. Add a matching block to STRINGS for every existing key (copy the
//      "en" block as a starting point, translate each value).
// Missing keys/languages fall back to English automatically — a partial
// translation degrades gracefully instead of breaking the page.

export const LANGUAGES = {
  en: { name: "English", dir: "ltr" },
  fr: { name: "Français", dir: "ltr" },
  tw: { name: "Twi", dir: "ltr" },
  ar: { name: "العربية", dir: "rtl" },
  zh: { name: "中文", dir: "ltr" },
};

export function getDir(lang) {
  return LANGUAGES[lang]?.dir || "ltr";
}

const STRINGS = {
  en: {
    "error.notFound": "Ticket not found. Please check your tracking link.",
    "error.loadFailed": "Unable to load ticket status. Please try again.",
    "loading": "Loading your ticket…",
    "errorTitle": "Ticket Not Found",
    "poweredBy": "AuraQueue Queue Tracking",
    "updated": "Updated {time}",
    "urgentTitle": "🔔 YOUR TURN NOW",
    "urgentSub": "Please go to",
    "accessMode.simple": "Easy Reading Mode",
    "accessMode.hearing": "Hearing Impairment Mode",
    "accessMode.visual": "Visual Assistance Mode",
    "accessMode.mobility": "Mobility Assistance",
    "accessMode.communication": "Communication Assistance",
    "status.waiting.label": "Waiting",
    "status.waiting.desc": "Please wait. We'll call your number soon.",
    "status.serving.label": "Being Served",
    "status.serving.desc": "Please go to your counter now!",
    "status.transferred.label": "Transferred",
    "status.transferred.desc": "You've been sent to another station.",
    "status.completed.label": "Complete",
    "status.completed.desc": "Your visit is complete. Thank you!",
    "status.abandoned.label": "Abandoned",
    "status.abandoned.desc": "This ticket has been marked as abandoned.",
    "status.expired.label": "Expired",
    "status.expired.desc": "This ticket expired at end of day.",
    "yourQueueNumber": "Your Queue Number",
    "priority.emergency": "🚨 Emergency",
    "priority.priority": "⚡ Priority",
    "position.label": "Position in Queue",
    "position.next": "You're next!",
    "position.ahead": "{n} ahead of you",
    "wait.label": "Est. Wait",
    "wait.minutes": "minutes",
    "serving.status": "Status",
    "serving.now": "Now!",
    "serving.beingServed": "Being served",
    "station.beingServedAt": "Being served at",
    "station.currentlyAt": "Currently at",
    "station.counter": "Counter: {name}",
    "mobility.title": "Mobility Assistance Requested",
    "mobility.body": "Staff have been notified. When it is your turn, a staff member will come to you — you do not need to walk to the counter.",
    "comm.title": "Communication Assistance",
    "comm.body": "Staff have been informed. A communication support card will be ready when you are called.",
    "journey.title": "Your Visit Journey",
    "journey.wait": "Wait: {n} min",
    "journey.done": "✓ Done",
    "journey.inProgress": "In progress",
    "journey.waiting": "Waiting",
    "completed.title": "Visit Complete",
    "completed.body": "Thank you for your visit. We hope you received great care.",
    "abandoned.expiredMsg": "This ticket has expired at end of day. Please collect a new ticket.",
    "abandoned.msg": "This ticket has been marked as abandoned. Please visit the front desk if you are still present.",
    "live.text": "Live — updates automatically every 10 seconds",
    "messages.title": "Messages",
    "reply.placeholder": "Type a message to staff…",
    "reply.send": "Send",
    "reply.sent": "Sent",
    "reply.failed": "Failed to send. Please try again.",
    "rating.title": "How was your visit?",
    "rating.subtitle": "Your feedback helps us improve.",
    "rating.commentPlaceholder": "Anything you'd like to add? (optional)",
    "rating.submit": "Submit Rating",
    "rating.thanks": "Thank you for your feedback!",
    "rating.failed": "Failed to submit rating. Please try again.",
    "staffRating.title": "How was {name} at {station}?",
    "staffRating.subtitle": "Rate the staff member who just helped you.",
    "staffRating.submit": "Submit",
    "staffRating.thanks": "Thanks for rating {name}!",
    "staffRating.failed": "Failed to submit rating. Please try again.",

    // Kiosk flow (language-select screen itself is shown pre-selection in
    // English+Twi together and is not part of this table — see KioskPage.js)
    "kiosk.resettingIn": "Resetting in",
    "kiosk.stillHere": "I'm still here",
    "kiosk.assistanceTitle": "Do you need any assistance?",
    "kiosk.assistanceSub": "Select the option that best describes your needs.",
    "kiosk.back": "← Back",
    "kiosk.continue": "Continue →",
    "kiosk.enterDetails": "Enter your details",
    "kiosk.change": "Change",
    "kiosk.pleaseWait": "Please wait…",
    "kiosk.getNumber": "Get Queue Number →",
    "kiosk.yourQueueNumber": "Your Queue Number",
    "kiosk.emergencyPriority": " Emergency Priority",
    "kiosk.priorityPatient": " Priority Patient",
    "kiosk.pleaseGoTo": "Please go to:",
    "kiosk.position": "Position",
    "kiosk.inQueue": "in queue",
    "kiosk.estWait": "Est. Wait",
    "kiosk.minutes": "minutes",
    "kiosk.scanToTrack": "Scan to track your number",
    "kiosk.waitToBeCalled": "Please wait to be called. You will receive an SMS when it's your turn.",
    "kiosk.doneIssueAnother": "Done — Issue another ticket",
    "kiosk.autoReset": "Screen resets automatically in 30 seconds",
    "kiosk.somethingWrong": "Something went wrong",
    "kiosk.tryAgain": "Try Again",
    "kiosk.loadingKiosk": "Loading kiosk…",
    "kiosk.select": "Select",
    "kiosk.fieldRequired": "{label} is required",
    "kiosk.phoneRequired": "Phone number is required",
    "kiosk.ticketFailed": "Failed to issue ticket. Please try again.",
    "kiosk.notAvailable": "This kiosk is not available. Please contact staff.",

    "field.name": "Full Name",
    "field.phone": "Phone Number",
    "field.reason": "Reason for Visit",
    "field.insurance": "Insurance / NHIS",
  },

  fr: {
    "error.notFound": "Ticket introuvable. Veuillez vérifier votre lien de suivi.",
    "error.loadFailed": "Impossible de charger le statut du ticket. Veuillez réessayer.",
    "loading": "Chargement de votre ticket…",
    "errorTitle": "Ticket introuvable",
    "poweredBy": "Suivi de file d'attente AuraQueue",
    "updated": "Mis à jour à {time}",
    "urgentTitle": "🔔 C'EST VOTRE TOUR",
    "urgentSub": "Veuillez vous rendre à",
    "accessMode.simple": "Mode lecture facile",
    "accessMode.hearing": "Mode malentendant",
    "accessMode.visual": "Mode assistance visuelle",
    "accessMode.mobility": "Assistance à la mobilité",
    "accessMode.communication": "Assistance à la communication",
    "status.waiting.label": "En attente",
    "status.waiting.desc": "Veuillez patienter. Nous appellerons votre numéro bientôt.",
    "status.serving.label": "En cours de service",
    "status.serving.desc": "Veuillez vous rendre à votre guichet maintenant !",
    "status.transferred.label": "Transféré",
    "status.transferred.desc": "Vous avez été envoyé(e) à un autre poste.",
    "status.completed.label": "Terminé",
    "status.completed.desc": "Votre visite est terminée. Merci !",
    "status.abandoned.label": "Abandonné",
    "status.abandoned.desc": "Ce ticket a été marqué comme abandonné.",
    "status.expired.label": "Expiré",
    "status.expired.desc": "Ce ticket a expiré en fin de journée.",
    "yourQueueNumber": "Votre numéro de file",
    "priority.emergency": "🚨 Urgence",
    "priority.priority": "⚡ Priorité",
    "position.label": "Position dans la file",
    "position.next": "Vous êtes le prochain !",
    "position.ahead": "{n} devant vous",
    "wait.label": "Attente estimée",
    "wait.minutes": "minutes",
    "serving.status": "Statut",
    "serving.now": "Maintenant !",
    "serving.beingServed": "En cours de service",
    "station.beingServedAt": "Service en cours à",
    "station.currentlyAt": "Actuellement à",
    "station.counter": "Guichet : {name}",
    "mobility.title": "Assistance à la mobilité demandée",
    "mobility.body": "Le personnel a été informé. Quand ce sera votre tour, un membre du personnel viendra vous voir — vous n'avez pas besoin de vous déplacer jusqu'au guichet.",
    "comm.title": "Assistance à la communication",
    "comm.body": "Le personnel a été informé. Une carte d'aide à la communication sera prête à votre appel.",
    "journey.title": "Votre parcours de visite",
    "journey.wait": "Attente : {n} min",
    "journey.done": "✓ Terminé",
    "journey.inProgress": "En cours",
    "journey.waiting": "En attente",
    "completed.title": "Visite terminée",
    "completed.body": "Merci pour votre visite. Nous espérons que vous avez reçu d'excellents soins.",
    "abandoned.expiredMsg": "Ce ticket a expiré en fin de journée. Veuillez prendre un nouveau ticket.",
    "abandoned.msg": "Ce ticket a été marqué comme abandonné. Veuillez vous rendre à l'accueil si vous êtes toujours présent(e).",
    "live.text": "En direct — mise à jour automatique toutes les 10 secondes",
    "messages.title": "Messages",
    "reply.placeholder": "Écrivez un message au personnel…",
    "reply.send": "Envoyer",
    "reply.sent": "Envoyé",
    "reply.failed": "Échec de l'envoi. Veuillez réessayer.",
    "rating.title": "Comment s'est passée votre visite ?",
    "rating.subtitle": "Votre avis nous aide à nous améliorer.",
    "rating.commentPlaceholder": "Souhaitez-vous ajouter un commentaire ? (facultatif)",
    "rating.submit": "Envoyer la note",
    "rating.thanks": "Merci pour votre avis !",
    "rating.failed": "Échec de l'envoi de la note. Veuillez réessayer.",
    "staffRating.title": "Comment était {name} à {station} ?",
    "staffRating.subtitle": "Notez le membre du personnel qui vient de vous aider.",
    "staffRating.submit": "Envoyer",
    "staffRating.thanks": "Merci d'avoir noté {name} !",
    "staffRating.failed": "Échec de l'envoi de la note. Veuillez réessayer.",

    "kiosk.resettingIn": "Réinitialisation dans",
    "kiosk.stillHere": "Je suis toujours là",
    "kiosk.assistanceTitle": "Avez-vous besoin d'assistance ?",
    "kiosk.assistanceSub": "Sélectionnez l'option qui décrit le mieux vos besoins.",
    "kiosk.back": "← Retour",
    "kiosk.continue": "Continuer →",
    "kiosk.enterDetails": "Entrez vos informations",
    "kiosk.change": "Modifier",
    "kiosk.pleaseWait": "Veuillez patienter…",
    "kiosk.getNumber": "Obtenir un numéro →",
    "kiosk.yourQueueNumber": "Votre numéro de file",
    "kiosk.emergencyPriority": " Priorité urgence",
    "kiosk.priorityPatient": " Patient prioritaire",
    "kiosk.pleaseGoTo": "Veuillez vous rendre à :",
    "kiosk.position": "Position",
    "kiosk.inQueue": "dans la file",
    "kiosk.estWait": "Attente estimée",
    "kiosk.minutes": "minutes",
    "kiosk.scanToTrack": "Scannez pour suivre votre numéro",
    "kiosk.waitToBeCalled": "Veuillez attendre d'être appelé(e). Vous recevrez un SMS quand ce sera votre tour.",
    "kiosk.doneIssueAnother": "Terminé — Prendre un autre ticket",
    "kiosk.autoReset": "L'écran se réinitialise automatiquement dans 30 secondes",
    "kiosk.somethingWrong": "Une erreur s'est produite",
    "kiosk.tryAgain": "Réessayer",
    "kiosk.loadingKiosk": "Chargement du kiosque…",
    "kiosk.select": "Sélectionner",
    "kiosk.fieldRequired": "{label} est requis",
    "kiosk.phoneRequired": "Le numéro de téléphone est requis",
    "kiosk.ticketFailed": "Impossible de délivrer le ticket. Veuillez réessayer.",
    "kiosk.notAvailable": "Ce kiosque n'est pas disponible. Veuillez contacter le personnel.",

    "field.name": "Nom complet",
    "field.phone": "Numéro de téléphone",
    "field.reason": "Motif de la visite",
    "field.insurance": "Assurance / NHIS",
  },

  // Twi terminology kept consistent with what already exists elsewhere in
  // the app (kiosk welcome text, SMS templates) rather than introducing a
  // different style — "queue number" / "ticket" kept as loanwords, matching
  // established usage.
  tw: {
    "error.notFound": "Yɛanhu ticket no. Yɛsrɛ wo hwɛ wo tracking link no.",
    "error.loadFailed": "Yɛantumi anhu ticket no tebea. Yɛsrɛ wo sɔ hwɛ bio.",
    "loading": "Yɛrekɔ wo ticket no mu…",
    "errorTitle": "Yɛanhu Ticket No",
    "poweredBy": "AuraQueue Queue Tracking",
    "updated": "Yɛsesaa wɔ {time}",
    "urgentTitle": "🔔 SEESEI NA WO TURN ABA",
    "urgentSub": "Yɛsrɛ wo kɔ",
    "accessMode.simple": "Akenkan a Ɛnyɛ Den",
    "accessMode.hearing": "Aso a Ɛnte Mmoa",
    "accessMode.visual": "Ani a Ɛnhu Mmoa",
    "accessMode.mobility": "Nantew Mmoa",
    "accessMode.communication": "Nkitahodi Mmoa",
    "status.waiting.label": "Wɔretwɛn",
    "status.waiting.desc": "Yɛsrɛ wo twɛn. Yɛbɛfrɛ wo number no nnansa yi ara.",
    "status.serving.label": "Wɔreyɛ Wo Adwuma",
    "status.serving.desc": "Yɛsrɛ wo kɔ wo counter no seesei!",
    "status.transferred.label": "Wɔasoma Wo",
    "status.transferred.desc": "Wɔasoma wo akɔ station foforo.",
    "status.completed.label": "Awie",
    "status.completed.desc": "Wo nsra no awie. Meda wo ase!",
    "status.abandoned.label": "Wɔagyaa Mu",
    "status.abandoned.desc": "Wɔahyɛ saa ticket yi sɛ wɔagyaa mu.",
    "status.expired.label": "Atwa Mu",
    "status.expired.desc": "Saa ticket yi atwa mu wɔ da no awiei.",
    "yourQueueNumber": "Wo Queue Number",
    "priority.emergency": "🚨 Emergency",
    "priority.priority": "⚡ Priority",
    "position.label": "Wo Kwan Wɔ Queue No Mu",
    "position.next": "Wo na wodi hɔ!",
    "position.ahead": "{n} wɔ w'anim",
    "wait.label": "Bɔhyɛ Bere",
    "wait.minutes": "sema",
    "serving.status": "Tebea",
    "serving.now": "Seesei!",
    "serving.beingServed": "Wɔreyɛ w'adwuma",
    "station.beingServedAt": "Wɔreyɛ w'adwuma wɔ",
    "station.currentlyAt": "Wowɔ",
    "station.counter": "Counter: {name}",
    "mobility.title": "Wo Bisaa Nantew Mmoa",
    "mobility.body": "Yɛaka akyerɛ adwumayɛfo. Sɛ wo turn du a, adwumayɛfo bi bɛba wo nkyɛn — ɛho nhia sɛ wonantew kɔ counter no.",
    "comm.title": "Nkitahodi Mmoa",
    "comm.body": "Yɛaka akyerɛ adwumayɛfo. Wɔbɛsiesie nkitahodi mmoa card ama wo ansa na wɔafrɛ wo.",
    "journey.title": "W'akwantu Wɔ Wo Nsra Mu",
    "journey.wait": "Bɔhyɛ Bere: {n} sema",
    "journey.done": "✓ Awie",
    "journey.inProgress": "Ɛrekɔ so",
    "journey.waiting": "Wɔretwɛn",
    "completed.title": "Nsra No Awie",
    "completed.body": "Meda wo ase sɛ wobaa ha. Yɛn ani agye sɛ wonyaa ɔhwɛfo pa.",
    "abandoned.expiredMsg": "Saa ticket yi atwa mu wɔ da no awiei. Yɛsrɛ wo fa ticket foforo.",
    "abandoned.msg": "Wɔahyɛ saa ticket yi sɛ wɔagyaa mu. Sɛ woda so wɔ ha a, yɛsrɛ wo kɔ front desk hɔ.",
    "live.text": "Ɛrekɔ so seesei — ɛsesa ne ho ho biara wɔ sekɛnd du (10) biara mu",
    "messages.title": "Nkra",
    "reply.placeholder": "Kyerɛw nkra ma adwumayɛfo…",
    "reply.send": "Soma",
    "reply.sent": "Yɛasoma",
    "reply.failed": "Yɛantumi ansoma. Yɛsrɛ wo sɔ hwɛ bio.",
    "rating.title": "Ɛyɛɛ dɛn wɔ wo nsra no mu?",
    "rating.subtitle": "Wo nsɛnkyerɛnne boa yɛn ma yɛyɛ adwuma pa.",
    "rating.commentPlaceholder": "Wowɔ biribi ka? (ɛho nhia)",
    "rating.submit": "Soma Rating",
    "rating.thanks": "Meda wo ase sɛ wode wo adwene maa yɛn!",
    "rating.failed": "Yɛantumi ansoma rating no. Yɛsrɛ wo sɔ hwɛ bio.",
    "staffRating.title": "Ɛyɛɛ dɛn wɔ {station} hɔ, {name} nkyɛn?",
    "staffRating.subtitle": "Rating saa obi a ɔboaa wo no.",
    "staffRating.submit": "Soma",
    "staffRating.thanks": "Meda wo ase sɛ wode {name} rating maa yɛn!",
    "staffRating.failed": "Yɛantumi ansoma rating no. Yɛsrɛ wo sɔ hwɛ bio.",

    "kiosk.resettingIn": "Ɛbɛsane mu",
    "kiosk.stillHere": "Mewɔ ha",
    "kiosk.assistanceTitle": "Wohia mmoa biara?",
    "kiosk.assistanceSub": "Paw nhwɛsoɔ a ɛkyerɛ wo hia no.",
    "kiosk.back": "← San kɔ",
    "kiosk.continue": "Kɔ so →",
    "kiosk.enterDetails": "Fa wo nsɛm ato hɔ",
    "kiosk.change": "Sesa",
    "kiosk.pleaseWait": "Hwɛ kakra…",
    "kiosk.getNumber": "Fa Queue Number →",
    "kiosk.yourQueueNumber": "Wo Queue Number",
    "kiosk.emergencyPriority": " Tufuhunu Kwan",
    "kiosk.priorityPatient": " Priority",
    "kiosk.pleaseGoTo": "Kɔ:",
    "kiosk.position": "Wo sɔr",
    "kiosk.inQueue": "queue mu",
    "kiosk.estWait": "Bɛtwɛn",
    "kiosk.minutes": "sema",
    "kiosk.scanToTrack": "Scan yɛ whu wo number",
    "kiosk.waitToBeCalled": "Hwɛ ma wɔbɛfrɛ wo. Wɔbɛsoma wo SMS bere a ɛba wo sɔr.",
    "kiosk.doneIssueAnother": "Wie — Fa ticket foforɔ",
    "kiosk.autoReset": "Screen bɛsane mu automatikɔli sema 30 mu",
    "kiosk.somethingWrong": "Biribi ankɔ yiye",
    "kiosk.tryAgain": "Sɔ Bio",
    "kiosk.loadingKiosk": "Ɛrekɔ kiosk no mu…",
    "kiosk.select": "Paw",
    "kiosk.fieldRequired": "{label} a hia",
    "kiosk.phoneRequired": "Phone number ho hia",
    "kiosk.ticketFailed": "Yɛantumi amfa ticket. Yɛsrɛ wo sɔ hwɛ bio.",
    "kiosk.notAvailable": "Kiosk yi nni hɔ seesei. Yɛsrɛ wo kasa kyerɛ adwumayɛfo.",

    "field.name": "Din Nyinaa",
    "field.phone": "Phone Number",
    "field.reason": "Nea Enti Woaba",
    "field.insurance": "Insurance / NHIS",
  },

  ar: {
    "error.notFound": "لم يتم العثور على التذكرة. يرجى التحقق من رابط التتبع الخاص بك.",
    "error.loadFailed": "تعذر تحميل حالة التذكرة. يرجى المحاولة مرة أخرى.",
    "loading": "جارٍ تحميل تذكرتك…",
    "errorTitle": "التذكرة غير موجودة",
    "poweredBy": "تتبع الطابور AuraQueue",
    "updated": "تم التحديث {time}",
    "urgentTitle": "🔔 دورك الآن",
    "urgentSub": "يرجى التوجه إلى",
    "accessMode.simple": "وضع القراءة السهلة",
    "accessMode.hearing": "وضع ضعف السمع",
    "accessMode.visual": "وضع المساعدة البصرية",
    "accessMode.mobility": "مساعدة الحركة",
    "accessMode.communication": "مساعدة التواصل",
    "status.waiting.label": "قيد الانتظار",
    "status.waiting.desc": "يرجى الانتظار. سنستدعي رقمك قريبًا.",
    "status.serving.label": "جارٍ تقديم الخدمة",
    "status.serving.desc": "يرجى التوجه إلى الشباك الآن!",
    "status.transferred.label": "تم التحويل",
    "status.transferred.desc": "تم إرسالك إلى محطة أخرى.",
    "status.completed.label": "مكتمل",
    "status.completed.desc": "انتهت زيارتك. شكرًا لك!",
    "status.abandoned.label": "متروك",
    "status.abandoned.desc": "تم تصنيف هذه التذكرة على أنها متروكة.",
    "status.expired.label": "منتهية الصلاحية",
    "status.expired.desc": "انتهت صلاحية هذه التذكرة في نهاية اليوم.",
    "yourQueueNumber": "رقم الطابور الخاص بك",
    "priority.emergency": "🚨 طارئ",
    "priority.priority": "⚡ أولوية",
    "position.label": "الموقع في الطابور",
    "position.next": "أنت التالي!",
    "position.ahead": "{n} أمامك",
    "wait.label": "الانتظار المقدر",
    "wait.minutes": "دقائق",
    "serving.status": "الحالة",
    "serving.now": "الآن!",
    "serving.beingServed": "جارٍ تقديم الخدمة",
    "station.beingServedAt": "جارٍ تقديم الخدمة في",
    "station.currentlyAt": "حاليًا في",
    "station.counter": "الشباك: {name}",
    "mobility.title": "تم طلب مساعدة الحركة",
    "mobility.body": "تم إخطار الموظفين. عندما يحين دورك، سيأتي أحد الموظفين إليك — لا حاجة للمشي إلى الشباك.",
    "comm.title": "مساعدة التواصل",
    "comm.body": "تم إبلاغ الموظفين. ستكون بطاقة دعم التواصل جاهزة عند استدعائك.",
    "journey.title": "رحلة زيارتك",
    "journey.wait": "الانتظار: {n} دقيقة",
    "journey.done": "✓ تم",
    "journey.inProgress": "قيد التنفيذ",
    "journey.waiting": "قيد الانتظار",
    "completed.title": "اكتملت الزيارة",
    "completed.body": "شكرًا لزيارتك. نأمل أن تكون قد تلقيت رعاية ممتازة.",
    "abandoned.expiredMsg": "انتهت صلاحية هذه التذكرة في نهاية اليوم. يرجى الحصول على تذكرة جديدة.",
    "abandoned.msg": "تم تصنيف هذه التذكرة على أنها متروكة. يرجى زيارة مكتب الاستقبال إذا كنت لا تزال موجودًا.",
    "live.text": "مباشر — يتم التحديث تلقائيًا كل 10 ثوانٍ",
    "messages.title": "الرسائل",
    "reply.placeholder": "اكتب رسالة إلى الموظفين…",
    "reply.send": "إرسال",
    "reply.sent": "تم الإرسال",
    "reply.failed": "فشل الإرسال. يرجى المحاولة مرة أخرى.",
    "rating.title": "كيف كانت زيارتك؟",
    "rating.subtitle": "رأيك يساعدنا على التحسين.",
    "rating.commentPlaceholder": "هل تود إضافة تعليق؟ (اختياري)",
    "rating.submit": "إرسال التقييم",
    "rating.thanks": "شكرًا لملاحظاتك!",
    "rating.failed": "فشل إرسال التقييم. يرجى المحاولة مرة أخرى.",
    "staffRating.title": "كيف كان {name} في {station}؟",
    "staffRating.subtitle": "قيّم الموظف الذي ساعدك للتو.",
    "staffRating.submit": "إرسال",
    "staffRating.thanks": "شكرًا لتقييمك {name}!",
    "staffRating.failed": "فشل إرسال التقييم. يرجى المحاولة مرة أخرى.",

    "kiosk.resettingIn": "إعادة التعيين خلال",
    "kiosk.stillHere": "لا زلت هنا",
    "kiosk.assistanceTitle": "هل تحتاج إلى أي مساعدة؟",
    "kiosk.assistanceSub": "اختر الخيار الذي يصف احتياجاتك بشكل أفضل.",
    "kiosk.back": "رجوع",
    "kiosk.continue": "متابعة",
    "kiosk.enterDetails": "أدخل بياناتك",
    "kiosk.change": "تغيير",
    "kiosk.pleaseWait": "يرجى الانتظار…",
    "kiosk.getNumber": "احصل على رقم الطابور",
    "kiosk.yourQueueNumber": "رقم الطابور الخاص بك",
    "kiosk.emergencyPriority": " أولوية طارئة",
    "kiosk.priorityPatient": " مريض ذو أولوية",
    "kiosk.pleaseGoTo": "يرجى التوجه إلى:",
    "kiosk.position": "الموقع",
    "kiosk.inQueue": "في الطابور",
    "kiosk.estWait": "الانتظار المقدر",
    "kiosk.minutes": "دقائق",
    "kiosk.scanToTrack": "امسح لتتبع رقمك",
    "kiosk.waitToBeCalled": "يرجى الانتظار حتى يتم استدعاؤك. ستتلقى رسالة نصية عندما يحين دورك.",
    "kiosk.doneIssueAnother": "تم — إصدار تذكرة أخرى",
    "kiosk.autoReset": "ستتم إعادة تعيين الشاشة تلقائيًا خلال 30 ثانية",
    "kiosk.somethingWrong": "حدث خطأ ما",
    "kiosk.tryAgain": "حاول مرة أخرى",
    "kiosk.loadingKiosk": "جارٍ تحميل الكشك…",
    "kiosk.select": "اختر",
    "kiosk.fieldRequired": "{label} مطلوب",
    "kiosk.phoneRequired": "رقم الهاتف مطلوب",
    "kiosk.ticketFailed": "فشل إصدار التذكرة. يرجى المحاولة مرة أخرى.",
    "kiosk.notAvailable": "هذا الكشك غير متاح. يرجى الاتصال بالموظفين.",

    "field.name": "الاسم الكامل",
    "field.phone": "رقم الهاتف",
    "field.reason": "سبب الزيارة",
    "field.insurance": "التأمين / NHIS",
  },

  zh: {
    "error.notFound": "未找到票据。请检查您的追踪链接。",
    "error.loadFailed": "无法加载票据状态。请重试。",
    "loading": "正在加载您的票据…",
    "errorTitle": "未找到票据",
    "poweredBy": "AuraQueue 排队追踪",
    "updated": "更新于 {time}",
    "urgentTitle": "🔔 现在轮到您了",
    "urgentSub": "请前往",
    "accessMode.simple": "简易阅读模式",
    "accessMode.hearing": "听力障碍模式",
    "accessMode.visual": "视力辅助模式",
    "accessMode.mobility": "行动不便协助",
    "accessMode.communication": "沟通协助",
    "status.waiting.label": "等候中",
    "status.waiting.desc": "请稍候。我们会尽快叫号。",
    "status.serving.label": "服务中",
    "status.serving.desc": "请立即前往您的柜台！",
    "status.transferred.label": "已转移",
    "status.transferred.desc": "您已被转至另一个站点。",
    "status.completed.label": "已完成",
    "status.completed.desc": "您的就诊已完成。谢谢！",
    "status.abandoned.label": "已放弃",
    "status.abandoned.desc": "此票据已被标记为放弃。",
    "status.expired.label": "已过期",
    "status.expired.desc": "此票据已于当天结束时过期。",
    "yourQueueNumber": "您的排队号码",
    "priority.emergency": "🚨 紧急",
    "priority.priority": "⚡ 优先",
    "position.label": "排队位置",
    "position.next": "您是下一位！",
    "position.ahead": "您前面还有 {n} 位",
    "wait.label": "预计等待",
    "wait.minutes": "分钟",
    "serving.status": "状态",
    "serving.now": "现在！",
    "serving.beingServed": "服务中",
    "station.beingServedAt": "正在此处接受服务",
    "station.currentlyAt": "当前位置",
    "station.counter": "柜台：{name}",
    "mobility.title": "已申请行动协助",
    "mobility.body": "工作人员已收到通知。轮到您时，工作人员会主动前来找您——您无需走到柜台。",
    "comm.title": "沟通协助",
    "comm.body": "工作人员已收到通知。叫到您时，沟通支持卡将已准备就绪。",
    "journey.title": "您的就诊流程",
    "journey.wait": "等待：{n} 分钟",
    "journey.done": "✓ 已完成",
    "journey.inProgress": "进行中",
    "journey.waiting": "等候中",
    "completed.title": "就诊已完成",
    "completed.body": "感谢您的到访。希望您得到了良好的照护。",
    "abandoned.expiredMsg": "此票据已于当天结束时过期。请领取新票据。",
    "abandoned.msg": "此票据已被标记为放弃。如果您仍在现场，请前往前台。",
    "live.text": "实时 — 每10秒自动更新",
    "messages.title": "留言",
    "reply.placeholder": "给工作人员发送消息…",
    "reply.send": "发送",
    "reply.sent": "已发送",
    "reply.failed": "发送失败，请重试。",
    "rating.title": "您对本次就诊满意吗？",
    "rating.subtitle": "您的反馈将帮助我们改进服务。",
    "rating.commentPlaceholder": "还有什么想说的吗？（可选）",
    "rating.submit": "提交评价",
    "rating.thanks": "感谢您的反馈！",
    "rating.failed": "评价提交失败，请重试。",
    "staffRating.title": "{station}的{name}服务怎么样？",
    "staffRating.subtitle": "为刚刚帮助您的工作人员评分。",
    "staffRating.submit": "提交",
    "staffRating.thanks": "感谢您对{name}的评价！",
    "staffRating.failed": "评价提交失败，请重试。",

    "kiosk.resettingIn": "重置倒计时",
    "kiosk.stillHere": "我还在",
    "kiosk.assistanceTitle": "您需要任何协助吗？",
    "kiosk.assistanceSub": "请选择最符合您需求的选项。",
    "kiosk.back": "← 返回",
    "kiosk.continue": "继续 →",
    "kiosk.enterDetails": "输入您的信息",
    "kiosk.change": "更改",
    "kiosk.pleaseWait": "请稍候…",
    "kiosk.getNumber": "获取排队号码 →",
    "kiosk.yourQueueNumber": "您的排队号码",
    "kiosk.emergencyPriority": " 紧急优先",
    "kiosk.priorityPatient": " 优先患者",
    "kiosk.pleaseGoTo": "请前往：",
    "kiosk.position": "位置",
    "kiosk.inQueue": "排队中",
    "kiosk.estWait": "预计等待",
    "kiosk.minutes": "分钟",
    "kiosk.scanToTrack": "扫描以追踪您的号码",
    "kiosk.waitToBeCalled": "请耐心等待叫号。轮到您时您将收到短信通知。",
    "kiosk.doneIssueAnother": "完成 — 领取另一张票据",
    "kiosk.autoReset": "屏幕将在30秒后自动重置",
    "kiosk.somethingWrong": "出现错误",
    "kiosk.tryAgain": "重试",
    "kiosk.loadingKiosk": "正在加载自助终端…",
    "kiosk.select": "选择",
    "kiosk.fieldRequired": "{label} 为必填项",
    "kiosk.phoneRequired": "电话号码为必填项",
    "kiosk.ticketFailed": "领取票据失败，请重试。",
    "kiosk.notAvailable": "此自助终端目前不可用，请联系工作人员。",

    "field.name": "姓名",
    "field.phone": "电话号码",
    "field.reason": "就诊原因",
    "field.insurance": "保险 / NHIS",
  },
};

/**
 * Translate a key for a given language. Falls back to English if the
 * language or the specific key isn't translated yet. {placeholders} are
 * substituted from `vars`.
 */
export function t(lang, key, vars) {
  const table = STRINGS[lang] || STRINGS.en;
  let str = table[key] ?? STRINGS.en[key] ?? key;
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => {
      str = str.replace(`{${k}}`, v);
    });
  }
  return str;
}

export function hasTranslation(lang) {
  return Boolean(STRINGS[lang]);
}
